import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { HealthUnitsInfoContainer } from './styles';
import Skeleton from 'react-loading-skeleton';
import { UnitCard } from './Card';
import { getUserProfile } from 'helpers/userProfile';
import { useHistory } from 'react-router-dom';
import { useHealthDataContext } from 'pages/NewOverview/contexts/HealthDataContext';
import { useFilterContext } from 'pages/NewOverview/contexts/FilterContext';
import { handleFilterStateRoute } from 'pages/NewOverview/helpers/filters';

interface HealthUnitsInfoProps {
  loading: boolean;
  hasHealth: boolean;
}

export function HealthUnitsInfo({
  hasHealth,
  loading,
}: Readonly<HealthUnitsInfoProps>): ReactElement {
  const [profile] = useState(getUserProfile());
  const history = useHistory();
  const { data, loading: loadingHealth, refetch } = useHealthDataContext();
  const { client, selectedOptions } = useFilterContext();

  const generalLoading = useMemo(() => loading || (loadingHealth && hasHealth), [hasHealth, loading, loadingHealth]);

  const hasPredictiveAccess = useMemo(() => {
    const ALLOWED_PREDICTIVE_CLIENT_IDS = new Set([145]);

    return profile.permissions.PER_CLIENT.some(
      (item) => ALLOWED_PREDICTIVE_CLIENT_IDS.has(item.clientId),
    );
  }, [profile.permissions.PER_CLIENT]);

  function onClickDetails(): void {
    if (hasPredictiveAccess) {
      history.push('/analise/preditivo');
    } else {
      history.push('/analise/maquinas?tipo=machines',
        handleFilterStateRoute(client, selectedOptions, profile));
    }
  }

  function onClickOfflineDetails(): void {
    if (hasPredictiveAccess) {
      history.push('/analise/conectividade');
    }
  }

  useEffect(() => {
    if (hasHealth) {
      refetch(Number(client), selectedOptions);
    }
  }, [
    selectedOptions.group?.units,
    selectedOptions.state,
    selectedOptions.healthStatus,
  ]);

  if (generalLoading) {
    return (
      <HealthUnitsInfoContainer>
        <Skeleton
          height="166px"
          borderRadius="8px"
        />
        <Skeleton
          height="166px"
          borderRadius="8px"
        />
        <Skeleton
          height="166px"
          borderRadius="8px"
        />
        <Skeleton
          height="166px"
          borderRadius="8px"
        />
      </HealthUnitsInfoContainer>
    );
  }

  return (
    <HealthUnitsInfoContainer data-testId="health-units-info">
      <UnitCard
        variant="healthy"
        onClickDetails={onClickDetails}
        value={hasHealth && !loading ? data?.healthy : undefined}
      />
      <UnitCard
        variant="alert"
        onClickDetails={onClickDetails}
        value={hasHealth && !loading ? data?.alert : undefined}
      />
      <UnitCard
        variant="critical"
        onClickDetails={onClickDetails}
        value={hasHealth && !loading ? data?.critical : undefined}
      />
      <UnitCard
        variant="inoperative"
        onClickDetails={hasPredictiveAccess ? onClickOfflineDetails : undefined}
        value={hasHealth && !loading ? data?.inoperative : undefined}
      />
    </HealthUnitsInfoContainer>
  );
}
